import { Box, Typography, Paper, Grid, Dialog, DialogTitle, DialogContent, IconButton, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import SensorsIcon from '@mui/icons-material/Sensors';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CloseIcon from '@mui/icons-material/Close';

interface CoreConceptsProps {
  open: boolean;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '1200px',
    width: '90%',
    margin: theme.spacing(2)
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  boxShadow: 'none',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left',
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const CoreConcepts = ({ open, onClose }: CoreConceptsProps) => {
  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3">Core Concepts</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12} sx={{ mb: -3 }}>
            <StyledPaper elevation={2}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <StyledIcon>
                  <InfoIcon sx={{ fontSize: 48 }} />
                </StyledIcon>
                <Typography variant="h4">
                  How It All Works Together
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                In the Brewry ecosystem, <strong>Cellars</strong> provide the organizational structure for your brewing operations. 
                Each Cellar can contain multiple <strong>Racks</strong> (physical devices) that collect data. 
                The data from these Racks is stored in <strong>Kegs</strong>, which act as data containers. 
                This hierarchical structure makes it easy to manage and monitor your entire brewing process, 
                from individual sensors to complete operations.
              </Typography>
            </StyledPaper>
          </Grid>

          <Grid item xs={4}>
            <StyledPaper elevation={2}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <StyledIcon>
                  <StorageIcon />
                </StyledIcon>
                <Typography variant="h4">
                  Cellar
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                A <strong>Cellar</strong> represents a specific use case or application within the Brewry platform. 
                Think of it as a container that organizes related devices and their data. 
                Each Cellar can contain multiple <strong>Racks</strong> and manage different aspects of your brewing operations.
              </Typography>
            </StyledPaper>
          </Grid>

          <Grid item xs={4}>
            <StyledPaper elevation={2}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <StyledIcon>
                  <SensorsIcon />
                </StyledIcon>
                <Typography variant="h4">
                  Rack
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                A <strong>Rack</strong> represents a physical device or sensor in the system. 
                It's the hardware component that collects and transmits data. 
                Racks can monitor various parameters like temperature, humidity, and pressure in your brewing environment.
              </Typography>
            </StyledPaper>
          </Grid>

          <Grid item xs={4}>
            <StyledPaper elevation={2}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <StyledIcon>
                  <LocalBarIcon />
                </StyledIcon>
                <Typography variant="h4">
                  Keg
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                A <strong>Keg</strong> represents the sensor data being collected. 
                It's the container for all telemetry and measurement data within the system. 
                Kegs store and organize the readings from your <strong>Racks</strong>, making it easy to track and analyze your brewing data.
              </Typography>
            </StyledPaper>
          </Grid>
        </Grid>

      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="outlined"
          color="primary"
          href="https://docs.brewry.xyz/"
          target="_blank"
          rel="noopener noreferrer"
        >
          View Documentation
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default CoreConcepts; 